import React from 'react';
import { FormattedMessage } from 'react-intl';
import type { RolesApi } from '@env0/role-service/api';
import styled from 'types/theme.types';
import { Item, Panel, PanelSection } from 'components/layout/navbar/navbar-panel.utils';

export interface VerticalTabItem {
  key: string;
  icon: React.ComponentType<any>;
  labelId: string;
  component: React.ComponentType<any>;
  showInMobile?: boolean;
  requiredPermission?: RolesApi.RBACPermission | RolesApi.RBACPermission[];
  isHiddenFeature?: boolean;
}

interface VerticalTabsMenuProps {
  items: VerticalTabItem[];
  activeKey?: string;
  onTabChange: (key: string) => void;
  componentProps?: any;
  dataE2e?: string;
}

const Content = styled.div`
  flex: 1;
  min-width: 0;
  padding: 25px 19px;
  overflow-y: scroll;
  box-sizing: border-box;
`;

const MenuItem = styled(Item)`
  /* Default text and icon color */
  color: ${({ theme }) => theme.primaryBlack};

  /* Override Item's nested SVG color/stroke to follow current text color */
  svg {
    color: currentColor;
    stroke: currentColor;
    stroke-width: 0.7px;

    * {
      stroke: currentColor !important;
      stroke-width: inherit;
    }
  }
  ${({ active, theme }) =>
    active &&
    `
      background-color: ${theme.sideMenuItemSelected};
      font-weight: bold;
      border-left: 3px solid ${theme.limeYellow};
      border-radius: 0;
    `}

  ${({ noHover, theme }) =>
    !noHover &&
    `
      &:hover {
        background-color: ${theme.sideMenuItemSelected};
        font-weigh: 500;
      }
    `}
`;

const VerticalTabsMenu: React.FC<VerticalTabsMenuProps> = ({
  items,
  activeKey,
  onTabChange,
  componentProps,
  dataE2e = 'tabs-settings'
}) => {
  const activeItem = items.find(item => item.key === activeKey);
  const ActiveComponent = activeItem?.component;

  return (
    <Container data-e2e={dataE2e}>
      <Sidebar>
        <Panel>
          <PanelSection>
            {items.map(item => {
              const Icon = item.icon;
              const isActive = item.key === activeKey;

              return (
                <MenuItem
                  key={item.key}
                  data-e2e={`menu-${item.key}`}
                  onClick={() => onTabChange(item.key)}
                  active={isActive}>
                  <Icon size={16} />
                  <span>
                    <FormattedMessage id={item.labelId} />
                  </span>
                </MenuItem>
              );
            })}
          </PanelSection>
        </Panel>
      </Sidebar>

      <Content>{ActiveComponent && componentProps && <ActiveComponent {...componentProps} />}</Content>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 150px;
  height: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.sideMenuBackground};
  box-sizing: border-box;
  border-right: 1px solid ${({ theme }) => theme.sideMenuBorder};
`;

export default VerticalTabsMenu;
